<template>
    <div class="dynamic-page">
        <transition
            mode="out-in"
            name="page-gate"
        >
            <age-gate-section
                v-if="page.age_gate && !ageConfirmed"
                @age-approved="confirmAge"
                key="age-gate"
            />
            <div
                v-else
                key="default"
            >
                <page-module-builder
                    :components="page.components"
                    :locale="locale"
                />
            </div>
        </transition>

        <global-events
            v-if="$route.query.preview"
            @message="handleMessage"
            target="window"
        />
    </div>
</template>

<script>
    import PageModuleBuilder from '../../page-modules/PageModuleBuilder/PageModuleBuilder';
    import generatePageHead from '../../../lib/generate-page-head';
    import { runPageModulesAsyncData } from '../../../lib/page-modules';
    import loading from '../../../lib/loading';
    import GlobalEvents from 'vue-global-events';
    import { ITSU_COOKIE_AGE_NAME } from '../../../plugins/constants';
    import AgeGateSection from '../../organisms/AgeGateSection/AgeGateSection.vue';

    export default {
        name: 'dynamic-page',

        components: { PageModuleBuilder, AgeGateSection, GlobalEvents },

        head() {
            return generatePageHead(this, this.page);
        },

        async asyncData(context) {
            try {
                const { $strapi, route, query, store } = context;

                const { path } = route;

                //eslint-disable-next-line no-console
                console.log(`Path: [${ path }]`);

                const pathLocale = path.split('/')[1] || 'en';

                const locale = store.state.locales.locales.find(({ code }) => code === pathLocale)?.code;

                loading().start();

                const page = await $strapi.fetchDynamicPage(path, {
                    previewDocumentId: query.preview,
                    locale,
                });

                if (!page) {
                    loading().fail();

                    return context.error({
                        statusCode: 404,
                    });
                }

                context.store.commit('locales/setPageLocales', [
                    {
                        locale: page.locale,
                        uri: page.uri,
                    },
                    ...page.localizations,
                ]);

                context.store.commit('locales/setLocale', page.locale || 'en');

                (page.components || []).forEach((component) => {
                    component.__page = {
                        id: page.documentId,
                        published_at: page?.publishedAt,
                    };
                });

                await runPageModulesAsyncData(page, context);

                loading().finish();

                return {
                    page,
                    locale: page.locale || 'en',
                };
            } catch (e) {
                loading().finish();

                //eslint-disable-next-line no-console
                console.log(e);

                context.error({
                    statusCode: 500,
                });
            }
        },

        data() {
            return {
                ageConfirmed: Boolean(this.$cookies.get(ITSU_COOKIE_AGE_NAME)),
            };
        },

        methods: {
            /**
             * Update ageConfirmed (so we don't have to refresh the page).
             */
            confirmAge() {
                this.ageConfirmed = true;
            },

            /**
             * Handle messages fro strapi so we can reload the page data.
             *
             * @param {object} message
             */
            handleMessage(message) {
                if (
                    message.origin === this.$config.API_URL
                    && message.data.type === 'strapiUpdate'
                ) {
                    this.$nuxt.refresh();
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .page-gate-enter-active,
    .page-gate-leave-active {
        transition: all 0.5s ease-in-out;
    }

    .page-gate-enter-from,
    .page-gate-leave-to {
        opacity: 0;
    }

    .page-gate-leave-to {
        transform: translateY(10px);
    }
</style>
